import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ClockIcon, 
  UserIcon,
  AcademicCapIcon,
  CreditCardIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const CourseEnrollment = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, studentId } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    const loadCourseData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load course details
        const courseData = await apiService.getCourseDetails(courseId);
        setCourse(courseData);

        // Check enrollment status if user is authenticated
        if (isAuthenticated && studentId) {
          try {
            const status = await apiService.getEnrollmentStatus(courseId);
            setEnrollmentStatus(status);
          } catch (error) {
            console.warn('Failed to check enrollment status:', error);
            // Set default status if check fails
            setEnrollmentStatus({
              isEnrolled: false,
              studentId: studentId,
              courseId: courseId
            });
          }
        }
      } catch (err) {
        console.error('Failed to load course data:', err);
        setError('Failed to load course information. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (courseId) {
      loadCourseData();
    }
  }, [courseId, isAuthenticated, studentId]);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/course/${courseId}` } });
      return;
    }

    try {
      setEnrolling(true);
      
      // Get available batches for this course
      const batches = await apiService.getCourseBatches(courseId);
      const activeBatches = batches.filter(batch => batch.status === 'active');
      
      if (activeBatches.length === 0) {
        setError('No active batches available for enrollment. Please contact support.');
        return;
      }

      // Enroll in the first available batch
      const batch = activeBatches[0];
      await apiService.enrollInBatch(batch.id);
      
      // Refresh enrollment status
      const status = await apiService.getEnrollmentStatus(courseId);
      setEnrollmentStatus(status);
      
      // Redirect to course learning page
      navigate(`/learn/${courseId}`);
    } catch (err) {
      console.error('Enrollment failed:', err);
      setError('Enrollment failed. Please try again or contact support.');
    } finally {
      setEnrolling(false);
    }
  };

  const getCurrencySymbol = (currency) => {
    switch ((currency || '').toUpperCase()) {
      case 'NGN': return '₦';
      case 'USD': return '$';
      case 'GBP': return '£';
      case 'EUR': return '€';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course information...</p>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-red-600 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Not Available</h2>
          <p className="text-gray-600 mb-6">{error || 'This course could not be loaded.'}</p>
          <button 
            onClick={() => navigate('/courses')} 
            className="btn-primary"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const formattedPrice = course.cost_amount && course.cost_currency
    ? `${getCurrencySymbol(course.cost_currency)}${Number(course.cost_amount).toLocaleString()}`
    : 'Contact for price';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Course Header */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-8 text-white">
            <h1 className="text-3xl font-bold mb-2">{course.name}</h1>
            <p className="text-primary-100 text-lg">{course.description}</p>
          </div>
          
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="flex items-center">
                <ClockIcon className="w-6 h-6 text-primary-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Duration</p>
                  <p className="font-semibold">{course.duration_months} months</p>
                </div>
              </div>
              <div className="flex items-center">
                <AcademicCapIcon className="w-6 h-6 text-primary-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Level</p>
                  <p className="font-semibold">All Levels</p>
                </div>
              </div>
              <div className="flex items-center">
                <UserIcon className="w-6 h-6 text-primary-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-500">Students</p>
                  <p className="font-semibold">{course.total_students_enrolled || 0}</p>
                </div>
              </div>
            </div>

            {/* Enrollment Status */}
            {enrollmentStatus && (
              <div className={`p-4 rounded-lg mb-6 ${
                enrollmentStatus.isEnrolled 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-yellow-50 border border-yellow-200'
              }`}>
                <div className="flex items-center">
                  {enrollmentStatus.isEnrolled ? (
                    <CheckCircleIcon className="w-6 h-6 text-green-600 mr-3" />
                  ) : (
                    <XCircleIcon className="w-6 h-6 text-yellow-600 mr-3" />
                  )}
                  <div>
                    <h3 className={`font-semibold ${
                      enrollmentStatus.isEnrolled ? 'text-green-800' : 'text-yellow-800'
                    }`}>
                      {enrollmentStatus.isEnrolled ? 'You are enrolled!' : 'Not enrolled'}
                    </h3>
                    <p className={`text-sm ${
                      enrollmentStatus.isEnrolled ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {enrollmentStatus.isEnrolled 
                        ? 'You have access to all course materials and live sessions.'
                        : 'Enroll now to access course materials and live sessions.'
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Course Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Lifetime access to course content</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Live interactive sessions</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Certificate of completion</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Community support</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Mobile and desktop access</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Benefits</h3>
                <ul className="space-y-2">
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Hands-on projects</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Industry best practices</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Career guidance</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Portfolio development</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                    <span>Job placement assistance</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              {enrollmentStatus?.isEnrolled ? (
                <button
                  onClick={() => navigate(`/learn/${courseId}`)}
                  className="btn-primary flex-1 flex items-center justify-center"
                >
                  <AcademicCapIcon className="w-5 h-5 mr-2" />
                  Continue Learning
                </button>
              ) : (
                <>
                  <button
                    onClick={handleEnroll}
                    disabled={enrolling}
                    className={`btn-primary flex-1 flex items-center justify-center ${
                      enrolling ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  >
                    {enrolling ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Enrolling...
                      </>
                    ) : (
                      <>
                        <CreditCardIcon className="w-5 h-5 mr-2" />
                        Enroll Now - {formattedPrice}
                      </>
                    )}
                  </button>
                  
                  {!isAuthenticated && (
                    <button
                      onClick={() => navigate('/login', { state: { from: `/course/${courseId}` } })}
                      className="btn-secondary flex-1"
                    >
                      Login to Enroll
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Security Badge */}
            <div className="mt-6 flex items-center justify-center text-sm text-gray-500">
              <ShieldCheckIcon className="w-4 h-4 mr-2" />
              <span>Secure enrollment • 30-day money-back guarantee</span>
            </div>
          </div>
        </div>

        {/* Course Description */}
        {course.write_up && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About This Course</h2>
            <div className="prose prose-lg max-w-none text-gray-700">
              <p className="whitespace-pre-line">{course.write_up}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseEnrollment;
